import React, { useCallback, useMemo, useState } from "react"

import { format } from "date-fns"
import { Gauge } from "lucide-react"

import {
  useCollectionsConfig,
  useLocations,
  useRouteInstancesForDate,
  useVehicleTypes,
} from "@/hooks/useCollections"
import { StatBand, type StatChip } from "@/components/shared/StatBand"
import {
  calculateRouteStats,
  describeVarianceBands,
  summariseAlarmCauses,
  summariseVariance,
} from "@/lib/statusDisplay"
import { useStableVariance } from "@/hooks/useStableVariance"
import { useSyncMetrics } from "@/hooks/useSyncMetrics"
import { routeCapacity, toCapacityStop } from "@/lib/loadFactor"
import { loadFactorTone } from "@/lib/tone"
import { Route, RouteInstance, RouteStats, toRouteLegacy } from "@/types/collections"
import { findVehicleType } from "@/lib/routeCostCalculation"

import RouteDetailsSheet from "./RouteDetailsSheet"
import RouteListPanel from "./RouteListPanel"
import RouteMapDisplay from "./RouteMapDisplay"

const MapView: React.FC = () => {
  const [selectedDate] = useState<Date>(new Date())
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null)
  // Lifted here so the status filter also drives the map and the stats bar.
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [alarmsOnly, setAlarmsOnly] = useState(false)
  const [listSortBy, setListSortBy] = useState<string>("none")

  const dateStr = format(selectedDate, "yyyy-MM-dd")
  const { data: routeInstancesData } = useRouteInstancesForDate(dateStr)
  const { data: vehicleTypesData } = useVehicleTypes()
  const { data: locationsData } = useLocations()
  const { data: configData } = useCollectionsConfig()

  const routes: Route[] = useMemo(() => {
    if (!routeInstancesData || routeInstancesData.length === 0) {
      return []
    }
    return routeInstancesData.map((ri: RouteInstance) => toRouteLegacy(ri))
  }, [routeInstancesData])

  // Cancelled routes are hidden unless selected in the Status filter. `now` ticks with the data so
  // the hysteresis has a stable clock.
  const { visibleRoutes, now } = useMemo(() => {
    const visible = statusFilter.includes("cancelled")
      ? routes
      : routes.filter((r) => r.status !== "cancelled")
    return { visibleRoutes: visible, now: new Date() }
  }, [routes, statusFilter])

  const stats: RouteStats = useMemo(() => {
    const baseStats = calculateRouteStats(visibleRoutes)

    let totalUsedCapacity = 0
    let totalCapacity = 0
    let hasActuals = false

    let totalUkPalletCapacity = 0
    let totalUkPalletUsed = 0

    if (vehicleTypesData?.data && locationsData?.data && visibleRoutes.length > 0) {
      visibleRoutes.forEach((route) => {
        const vehicleTypeName = route.vehicleType
        let vehicleType = vehicleTypeName
          ? findVehicleType(vehicleTypesData.data, vehicleTypeName)
          : null
        // Ad-hoc routes often have no planned_route; fall back to the first vehicle type so the %
        // matches the Data tab.
        if (!vehicleType && route.isAdhoc && vehicleTypesData.data.length > 0) {
          vehicleType = vehicleTypesData.data[0]
        }
        if (!vehicleType) return

        const routeHasActuals = route.stops.some(
          (s) =>
            s.loadingUnitsActual !== undefined &&
            s.loadingUnitsActual !== null &&
            s.loadingUnitsActual > 0,
        )
        if (routeHasActuals) hasActuals = true

        const hasShipperCollection = route.stops.some(
          (stop) => stop.slotType === "shipper_collection",
        )

        if (!hasShipperCollection) return

        // preferActuals: the band reports what happened, so a served stop counts its actuals.
        const capacity = routeCapacity({
          vehicleType,
          stops: route.stops.map((stop) => toCapacityStop(stop, { preferActuals: true })),
          locations: locationsData.data,
          config: configData?.data,
        })
        if (capacity) {
          totalUsedCapacity += capacity.totalFillPercentage
          totalCapacity += 1

          // Summed, not averaged, so a van can't weigh as much as an HGV.
          if (capacity.ukPalletCapacity > 0) {
            totalUkPalletCapacity += capacity.ukPalletCapacity
            totalUkPalletUsed += capacity.ukPalletsUsed
          }
        }
      })
    }

    const fillPercentage =
      totalUkPalletCapacity > 0
        ? (totalUkPalletUsed / totalUkPalletCapacity) * 100
        : totalCapacity > 0
          ? totalUsedCapacity / totalCapacity
          : 0

    const overallCapacity =
      totalCapacity > 0
        ? {
            totalUsed: 0,
            totalCapacity: totalCapacity,
            totalRemaining: 0,
            fillPercentage: fillPercentage,
            basedOnActuals: hasActuals,
            ukPalletEquivalent:
              totalUkPalletCapacity > 0
                ? {
                    totalCapacity: totalUkPalletCapacity,
                    totalUsed: totalUkPalletUsed,
                  }
                : undefined,
          }
        : undefined

    return {
      ...baseStats,
      overallVehicleCapacity: overallCapacity,
    }
  }, [visibleRoutes, vehicleTypesData, locationsData, configData])

  const syncMetrics = useSyncMetrics(now)
  const stableVariance = useStableVariance(visibleRoutes, now)

  const variance = useMemo(
    () => ({
      ...summariseVariance(visibleRoutes, now, stableVariance),
      alarmCauses: summariseAlarmCauses(visibleRoutes, now, { varianceOf: stableVariance }),
      breakdown: describeVarianceBands(visibleRoutes, now, {
        varianceOf: stableVariance,
      }),
    }),
    [visibleRoutes, now, stableVariance],
  )

  /** Not what the band summarises: the band always describes the whole day. */
  const displayedRoutes = useMemo(() => {
    if (!alarmsOnly) return visibleRoutes
    return visibleRoutes.filter((r) => {
      const band = stableVariance(r)
      return band === "at_risk" || band === "late"
    })
  }, [visibleRoutes, alarmsOnly, stableVariance])

  const statChips = useMemo(() => {
    const chips: StatChip[] = []
    const capacity = stats.overallVehicleCapacity
    if (capacity) {
      const pallets = capacity.ukPalletEquivalent
      chips.push({
        key: "load_factor",
        icon: <Gauge size={12} />,
        label: `${capacity.fillPercentage.toFixed(0)}% load`,
        hint: pallets
          ? `${pallets.totalUsed.toFixed(1)}/${pallets.totalCapacity.toFixed(0)} pallets`
          : undefined,
        tone: loadFactorTone(capacity.fillPercentage),
      })
    }
    return chips
  }, [stats])

  const handleShowAlarms = useCallback(() => {
    setAlarmsOnly((on) => {
      const next = !on
      setListSortBy((sort) => (next ? "concern" : sort === "concern" ? "none" : sort))
      return next
    })
  }, [])

  const handleRouteSelect = (route: Route) => {
    setSelectedRoute(route)
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-palette-jungleGreen000">
      <StatBand
        lede={`${stats.totalRoutes} ${stats.totalRoutes === 1 ? "route" : "routes"}`}
        variance={variance}
        onShowAlarms={handleShowAlarms}
        alarmsShown={alarmsOnly}
        chips={statChips}
        metrics={syncMetrics}
      />

      <div className="flex-1 flex min-h-0 overflow-hidden">
        <RouteListPanel
          routes={displayedRoutes}
          selectedRoute={selectedRoute}
          onRouteSelect={handleRouteSelect}
          onRouteClick={handleRouteSelect}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          sortBy={listSortBy}
          onSortByChange={setListSortBy}
        />

        {/* The details panel is a viewport overlay, not a flex sibling. */}
        <div className="flex-1 flex min-h-0 min-w-0">
          <div className="flex-1 relative min-w-0 min-h-0">
            <RouteMapDisplay
              routes={displayedRoutes}
              selectedRoute={selectedRoute}
              onRouteSelect={handleRouteSelect}
              onRouteClick={handleRouteSelect}
              sidePanelOpen={!!selectedRoute}
            />
          </div>
        </div>
      </div>

      {selectedRoute && (
        <RouteDetailsSheet
          route={selectedRoute}
          open={true}
          onOpenChange={() => setSelectedRoute(null)}
          viewDate={selectedDate}
          hostShowsRouteMap
          onCancelled={() => setSelectedRoute(null)}
          onDuplicated={() => setSelectedRoute(null)}
        />
      )}
    </div>
  )
}

export default MapView
